import { Link, Button, Badge, Card, CardContent, useAuth, ArrowRight } from "@/pages/shared";
import { Helmet } from "react-helmet-async";
import { CLAIMS } from "@/config/claims";

const StatusBadge = ({ row }) => {
  if (row.autoPublish) return <Badge className="bg-teal-500/15 text-teal-400 border-teal-500/30">Auto-publish</Badge>;
  if (row.pending) return <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30">Pending Meta review</Badge>;
  return <Badge className="bg-white/10 text-muted-foreground border-white/15">Manual / export</Badge>;
};

export default function IntegrationsPage() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>Integrations — exactly what Creatara can publish, per network</title>
        <meta name="description" content="The honest integration matrix: which networks Creatara can auto-publish to, what each requires, and every known limitation." />
        <link rel="canonical" href="https://creatara.ai/integrations" />
      </Helmet>

      <nav className="border-b border-white/5 sticky top-0 z-40 bg-background/80 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold">Creatara</Link>
          <Link to={user ? "/dashboard" : "/register"}><Button className="bg-violet-600 hover:bg-violet-700 rounded-full" data-testid="integrations-cta-btn">{user ? "Dashboard" : "Get started free"}</Button></Link>
        </div>
      </nav>

      <section className="pt-16 pb-8 px-6">
        <div className="max-w-6xl mx-auto">
          <Badge className="mb-4 bg-violet-600/10 text-violet-500 border-violet-600/20">Integration Matrix</Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight">What Creatara can publish,<br className="hidden sm:block" /> network by network</h1>
          <p className="text-muted-foreground max-w-2xl">No marketing gloss. "Auto-publish" below means Creatara genuinely posts to <em>your own account</em> at the scheduled time with no human action. Everything else is labelled exactly as it works today.</p>
        </div>
      </section>

      <section className="px-6 pb-16">
        <div className="max-w-6xl mx-auto overflow-x-auto">
          <table className="w-full text-sm border-separate border-spacing-y-2" data-testid="integration-matrix-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-2">Network</th>
                <th className="px-4 py-2">Supported actions</th>
                <th className="px-4 py-2">Plan required</th>
                <th className="px-4 py-2">Account / OAuth requirements</th>
                <th className="px-4 py-2">Known limitations</th>
              </tr>
            </thead>
            <tbody>
              {CLAIMS.integrations.map((row) => (
                <tr key={row.network} className="bg-white/[0.03] align-top" data-testid={`integration-row-${row.network.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <td className="px-4 py-4 rounded-l-lg font-semibold whitespace-nowrap">
                    {row.network}
                    <div className="mt-2"><StatusBadge row={row} /></div>
                  </td>
                  <td className="px-4 py-4 text-muted-foreground min-w-[160px]">{row.actions}</td>
                  <td className="px-4 py-4 text-muted-foreground whitespace-nowrap">{row.plan}</td>
                  <td className="px-4 py-4 text-muted-foreground min-w-[240px]">{row.requirements}</td>
                  <td className="px-4 py-4 text-muted-foreground rounded-r-lg min-w-[260px]">{row.limitations}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="px-6 pb-20">
        <div className="max-w-3xl mx-auto">
          <Card className="glass border-amber-500/20" data-testid="integrations-meta-note">
            <CardContent className="p-6 text-sm text-muted-foreground leading-relaxed">
              <p className="font-semibold text-foreground mb-2">A note on Facebook & Instagram</p>
              <p>Per-user auto-publishing to Facebook Pages and Instagram accounts requires Meta App Review, which is in progress. Until it's approved, treat Facebook and Instagram as one-click manual share / export. We update this page the moment that changes.</p>
            </CardContent>
          </Card>
          <div className="text-center mt-10">
            <Link to={user ? "/dashboard/settings" : "/register"}>
              <Button size="lg" className="bg-violet-600 hover:bg-violet-700 rounded-full px-8" data-testid="integrations-bottom-cta">
                {user ? "Connect your accounts" : "Start free"} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <p className="text-xs text-muted-foreground mt-3">Free plan: {CLAIMS.freeAllowance.generations} generations / {CLAIMS.freeAllowance.period}. No card required.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
